import React, {useState} from 'react';
import {useTranslation} from 'react-i18next';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    IconButton,
    MenuItem,
    Select,
    Stack,
    Switch,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
    useTheme
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import HorizontalSplitIcon from '@mui/icons-material/HorizontalSplit';
import ViewSidebarIcon from '@mui/icons-material/ViewSidebar';
import {
    AnyQuestion,
    EndScreen,
    Form,
    LayoutType,
    QuestionType,
    RatingQuestion,
    ShortTextQuestion,
    UploadMediaQuestion
} from 'crqlar-react-common/dist/models/forms/form';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import CColorPicker from "crqlar-react-common/dist/components/CColorPicker";
import ImagePicker from "crqlar-react-common/dist/components/ImagePicker";
import {Media} from "crqlar-react-common/dist/models/common/media.model";

interface QuestionSettingsPanelProps {
    question: AnyQuestion;
    form: Form;
    onQuestionUpdate: (question: AnyQuestion) => void;
}

const QuestionSettingsPanel: React.FC<QuestionSettingsPanelProps> = ({
                                                                         question,
                                                                         form,
                                                                         onQuestionUpdate
                                                                     }) => {
    const theme = useTheme();
    const {t} = useTranslation();
    const [imageDialogOpen, setImageDialogOpen] = useState(false);
    const [styleOverridesExpanded, setStyleOverridesExpanded] = useState(false);

    // Handle required toggle
    const handleRequiredChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        onQuestionUpdate({
            ...question,
            required: event.target.checked,
        });
    };

    // Handle layout change
    const handleLayoutChange = (_event: React.MouseEvent<HTMLElement>, newLayout: LayoutType | null) => {
        if (newLayout !== null) {
            onQuestionUpdate({
                ...question,
                layout: newLayout,
            });
        }
    };

    // Handle theme override changes
    const handleThemeOverride = (field: string, value: any) => {
        const newTheme = question.theme || {};

        if (field.startsWith('palette.')) {
            const paletteField = field.replace('palette.', '');
            onQuestionUpdate({
                ...question,
                theme: {
                    ...newTheme,
                    palette: {
                        ...newTheme.palette,
                        [paletteField]: value
                    }
                }
            });
        } else if (field === 'backgroundMedia') {
            onQuestionUpdate({
                ...question,
                theme: {
                    ...newTheme,
                    backgroundMedia: value
                }
            });
        }
    };

    // Reset all theme overrides
    const handleResetTheme = () => {
        const {theme: _removed, ...questionWithoutTheme} = question;
        onQuestionUpdate(questionWithoutTheme as AnyQuestion);
    };

    // Handle image change
    const handleImageChange = (media?: Media) => {
        handleThemeOverride('backgroundMedia', media);
        setImageDialogOpen(false);
    };

    // Check if question type should show required toggle
    const showRequiredToggle = question.type !== QuestionType.WELCOME &&
        question.type !== QuestionType.END_SCREEN

    // Check if this is a rating question
    const isRatingQuestion = question.type === QuestionType.RATING;

    // Check if this is an end screen
    const isEndScreen = question.type === QuestionType.END_SCREEN;

    // Check if this is a short text question
    const isShortTextQuestion = question.type === QuestionType.SHORT_TEXT;

    // Check if this is an upload media question
    const isUploadMediaQuestion = question.type === QuestionType.UPLOAD_MEDIA;

    // Handle max rating change for rating questions
    const handleMaxRatingChange = (event: any) => {
        if (isRatingQuestion) {
            onQuestionUpdate({
                ...question,
                maxRating: event.target.value as number
            } as RatingQuestion);
        }
    };

    // Handle end screen settings
    const handleCollectEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (isEndScreen) {
            onQuestionUpdate({
                ...question,
                collectEmail: event.target.checked
            } as EndScreen);
        }
    };

    const handleEmailPlaceholderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (isEndScreen) {
            onQuestionUpdate({
                ...question,
                emailPlaceholder: event.target.value
            } as EndScreen);
        }
    };

    const handleButtonTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (isEndScreen) {
            onQuestionUpdate({
                ...question,
                buttonText: event.target.value
            } as EndScreen);
        }
    };

    const handleRedirectUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (isEndScreen) {
            onQuestionUpdate({
                ...question,
                redirectUrl: event.target.value
            } as EndScreen);
        }
    };

    const handleRedirectWaitSecondsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (isEndScreen) {
            const value = event.target.value;
            onQuestionUpdate({
                ...question,
                redirectWaitSeconds: value === '' ? undefined : parseInt(value, 10)
            } as EndScreen);
        }
    };

    // Handle purpose change for short text questions
    const handlePurposeChange = (event: any) => {
        if (isShortTextQuestion) {
            onQuestionUpdate({
                ...question,
                purpose: event.target.value as 'text' | 'email' | 'tel'
            } as ShortTextQuestion);
        }
    };

    // Handle upload media settings
    const handleAllowMultipleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (isUploadMediaQuestion) {
            onQuestionUpdate({
                ...question,
                allowMultiple: event.target.checked
            } as UploadMediaQuestion);
        }
    };

    const handleAllowedTypesChange = (type: 'image' | 'video' | 'file') => {
        if (isUploadMediaQuestion) {
            const currentTypes = (question as UploadMediaQuestion).allowedTypes || ['image', 'video', 'file'];
            let newTypes: ('image' | 'video' | 'file')[];

            if (currentTypes.includes(type)) {
                newTypes = currentTypes.filter(t => t !== type);
            } else {
                newTypes = [...currentTypes, type];
            }

            // Ensure at least one type is selected
            if (newTypes.length === 0) {
                newTypes = [type];
            }

            onQuestionUpdate({
                ...question,
                allowedTypes: newTypes
            } as UploadMediaQuestion);
        }
    };

    return (
        <Box
            sx={{
                width: 320,
                height: '100%',
                borderLeft: `1px solid ${theme.palette.divider}`,
                backgroundColor: theme.palette.background.paper,
                overflowY: 'auto',
                p: 3,
            }}
        >
            {/*/!* Video Media Selector - Only for Welcome Screens *!/*/}
            {/*{isWelcomeScreen && (*/}
            {/*    <>*/}
            {/*        <Box sx={{mb: 3}}>*/}
            {/*            <Typography variant="body2" gutterBottom sx={{mb: 1}}>*/}
            {/*                Background Video*/}
            {/*            </Typography>*/}
            {/*            <Button*/}
            {/*                variant="outlined"*/}
            {/*                fullWidth*/}
            {/*                startIcon={<AddPhotoAlternateIcon/>}*/}
            {/*                onClick={() => setVideoDialogOpen(true)}*/}
            {/*                sx={{*/}
            {/*                    justifyContent: 'flex-start',*/}
            {/*                    py: 1.5,*/}
            {/*                }}*/}
            {/*            >*/}
            {/*                {(question as WelcomeScreen).videoMedia ? 'Change Video' : 'Add Video'}*/}
            {/*            </Button>*/}
            {/*        </Box>*/}
            {/*        <Divider sx={{my: 2}}/>*/}
            {/*    </>*/}
            {/*)}*/}

            {/* Required Toggle */}
            {showRequiredToggle && (
                <>
                    <FormControlLabel
                        control={
                            <Switch
                                checked={question.required || false}
                                onChange={handleRequiredChange}
                            />
                        }
                        label={<Typography variant="body2">{t('Required')}</Typography>}
                        sx={{mb: 3}}
                    />
                    <Divider sx={{my: 2}}/>
                </>
            )}

            {/* Short Text Purpose Selector */}
            {isShortTextQuestion && (
                <>
                    <Box sx={{mb: 3}}>
                        <Typography variant="body2" gutterBottom sx={{mb: 1}}>
                            {t('Input Type')}
                        </Typography>
                        <FormControl fullWidth size="small">
                            <Select
                                value={(question as ShortTextQuestion).purpose || 'text'}
                                onChange={handlePurposeChange}
                            >
                                <MenuItem value="text">{t('Text')}</MenuItem>
                                <MenuItem value="email">{t('Email')}</MenuItem>
                                <MenuItem value="tel">{t('Phone Number')}</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                    <Divider sx={{my: 2}}/>
                </>
            )}

            {/* Upload Media Settings */}
            {isUploadMediaQuestion && (
                <>
                    <Box sx={{mb: 3}}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={(question as UploadMediaQuestion).allowMultiple || false}
                                    onChange={handleAllowMultipleChange}
                                />
                            }
                            label={<Typography variant="body2">{t('Allow multiple file uploads')}</Typography>}
                            sx={{mb: 2}}
                        />

                        <Typography variant="body2" gutterBottom sx={{mb: 1}}>
                            {t('Allowed File Types')}
                        </Typography>
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={((question as UploadMediaQuestion).allowedTypes || ['image', 'video', 'file']).includes('image')}
                                        onChange={() => handleAllowedTypesChange('image')}
                                    />
                                }
                                label={<Typography variant="body2">{t('Images')}</Typography>}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={((question as UploadMediaQuestion).allowedTypes || ['image', 'video', 'file']).includes('video')}
                                        onChange={() => handleAllowedTypesChange('video')}
                                    />
                                }
                                label={<Typography variant="body2">{t('Videos')}</Typography>}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={((question as UploadMediaQuestion).allowedTypes || ['image', 'video', 'file']).includes('file')}
                                        onChange={() => handleAllowedTypesChange('file')}
                                    />
                                }
                                label={<Typography variant="body2">{t('Files/Documents')}</Typography>}
                            />
                        </FormGroup>
                    </Box>
                    <Divider sx={{my: 2}}/>
                </>
            )}

            {/*/!* Legal Question Settings *!/*/}
            {/*{isLegalQuestion && (*/}
            {/*    <>*/}
            {/*        <Box sx={{ mb: 3 }}>*/}
            {/*            <FormControlLabel*/}
            {/*                control={*/}
            {/*                    <Switch*/}
            {/*                        checked={(question as LegalQuestion).requireSignature || false}*/}
            {/*                        onChange={handleRequireSignatureChange}*/}
            {/*                    />*/}
            {/*                }*/}
            {/*                label={<Typography variant="body2">Require digital signature</Typography>}*/}
            {/*                sx={{ mb: 1 }}*/}
            {/*            />*/}
            {/*            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>*/}
            {/*                When enabled, users must sign instead of checking a box*/}
            {/*            </Typography>*/}
            {/*        </Box>*/}
            {/*        <Divider sx={{my: 2}}/>*/}
            {/*    </>*/}
            {/*)}*/}

            {/* End Screen Settings */}
            {isEndScreen && (
                <>
                    <Box sx={{mb: 3}}>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={(question as EndScreen).collectEmail || false}
                                    onChange={handleCollectEmailChange}
                                />
                            }
                            label={<Typography variant="body2">{t('Collect email address')}</Typography>}
                            sx={{mb: 2}}
                        />

                        {(question as EndScreen).collectEmail && (
                            <TextField
                                fullWidth
                                label={t('Email Placeholder')}
                                value={(question as EndScreen).emailPlaceholder || ''}
                                onChange={handleEmailPlaceholderChange}
                                variant="outlined"
                                size="small"
                                placeholder={t('Enter your email')}
                                sx={{mb: 2}}
                            />
                        )}

                        <TextField
                            fullWidth
                            label={t('Button Text')}
                            value={(question as EndScreen).buttonText || ''}
                            onChange={handleButtonTextChange}
                            variant="outlined"
                            size="small"
                            placeholder={t('Submit')}
                            sx={{mb: 2}}
                        />

                        <TextField
                            fullWidth
                            label={t('Redirect URL (optional)')}
                            value={(question as EndScreen).redirectUrl || ''}
                            onChange={handleRedirectUrlChange}
                            variant="outlined"
                            size="small"
                            placeholder="https://example.com/thank-you"
                            helperText={t('Where to redirect users after form submission')}
                            sx={{mb: 2}}
                        />

                        {(question as EndScreen).redirectUrl && (
                            <TextField
                                fullWidth
                                label={t('Redirect Wait Time (seconds)')}
                                type="number"
                                value={(question as EndScreen).redirectWaitSeconds ?? ''}
                                onChange={handleRedirectWaitSecondsChange}
                                variant="outlined"
                                size="small"
                                placeholder="5"
                                helperText={t('Seconds to wait before redirect (0 = immediate, default: 5)')}
                                inputProps={{min: 0}}
                            />
                        )}
                    </Box>
                    <Divider sx={{my: 2}}/>
                </>
            )}

            {/* Rating Type Selector */}
            {isRatingQuestion && (
                <>
                    <Box sx={{mb: 3}}>
                        <Typography variant="body2" gutterBottom sx={{mb: 1}}>
                            {t('Rating Type')}
                        </Typography>
                        <FormControl fullWidth size="small">
                            <Select
                                value={(question as RatingQuestion).maxRating || 10}
                                onChange={handleMaxRatingChange}
                            >
                                <MenuItem value={5}>{t('5 Stars')}</MenuItem>
                                <MenuItem value={10}>{t('10 Scale')}</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                    <Divider sx={{my: 2}}/>
                </>
            )}

            {/* Layout Section */}
            <Typography variant="h6" gutterBottom>{t('Layout')}</Typography>
            <ToggleButtonGroup
                value={question.layout || LayoutType.DEFAULT}
                exclusive
                onChange={handleLayoutChange}
                fullWidth
                size="small"
                sx={{mb: 1}}
            >
                <ToggleButton value={LayoutType.DEFAULT} aria-label={t('Default layout')}>
                    <CropSquareIcon fontSize="small"/>
                </ToggleButton>
                <ToggleButton value={LayoutType.TOP_SPLIT} aria-label={t('Top split layout')}>
                    <HorizontalSplitIcon fontSize="small"/>
                </ToggleButton>
                <ToggleButton value={LayoutType.LEFT_SPLIT} aria-label={t('Left split layout')}>
                    <ViewSidebarIcon fontSize="small"/>
                </ToggleButton>
                <ToggleButton value={LayoutType.RIGHT_SPLIT} aria-label={t('Right split layout')}>
                    <ViewSidebarIcon fontSize="small" sx={{transform: 'scaleX(-1)'}}/>
                </ToggleButton>
            </ToggleButtonGroup>

            {/* Warning message for side splits */}
            {(question.layout === LayoutType.LEFT_SPLIT || question.layout === LayoutType.RIGHT_SPLIT) && (
                <Typography
                    variant="caption"
                    sx={{
                        display: 'block',
                        color: 'text.secondary',
                        fontSize: '0.7rem',
                        fontStyle: 'italic',
                        mb: 2,
                        mt: 0.5
                    }}
                >
                    {t('Side splits only visible on screens ≥600px (tablets, laptops, desktops)')}
                </Typography>
            )}

            <Divider sx={{my: 2}}/>

            {/* Style Overrides Section */}
            <Accordion
                expanded={styleOverridesExpanded}
                onChange={() => setStyleOverridesExpanded(!styleOverridesExpanded)}
                sx={{mb: 2, boxShadow: 'none', '&:before': {display: 'none'}}}
            >
                <AccordionSummary
                    expandIcon={<ExpandMoreIcon/>}
                    sx={{px: 0}}
                >
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                        pr: 1
                    }}>
                        <Typography variant="h6">{t('Style Overrides')}</Typography>
                        {question.theme && (
                            <IconButton
                                size="small"
                                aria-label={t('Reset style overrides')}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleResetTheme();
                                }}
                                sx={{mr: 1}}
                            >
                                <RestartAltIcon fontSize="small"/>
                            </IconButton>
                        )}
                    </Box>
                </AccordionSummary>
                <AccordionDetails sx={{px: 0}}>
                    <Stack spacing={2}>
                        <Box>
                            <Typography variant="body2" gutterBottom>{t('Background Image')}</Typography>
                            <Button
                                variant="outlined"
                                fullWidth
                                startIcon={<AddPhotoAlternateIcon/>}
                                onClick={() => setImageDialogOpen(true)}
                                sx={{
                                    justifyContent: 'flex-start',
                                    py: 1.5,
                                }}
                            >
                                {question.theme?.backgroundMedia ? t('Change Image') : t('Add Image')}
                            </Button>
                        </Box>

                        <Box>
                            <Typography variant="body2" gutterBottom>{t('Primary Color')}</Typography>
                            <CColorPicker
                                value={question.theme?.palette?.primary}
                                onChange={color => handleThemeOverride('palette.primary', color)}
                            />
                        </Box>

                        <Box>
                            <Typography variant="body2" gutterBottom>{t('Form Background Color')}</Typography>
                            <CColorPicker
                                value={question.theme?.palette?.background}
                                onChange={color => handleThemeOverride('palette.background', color)}
                            />
                        </Box>

                        <Box>
                            <Typography variant="body2" gutterBottom>{t('Primary Text Color')}</Typography>
                            <CColorPicker
                                value={question.theme?.palette?.textPrimary}
                                onChange={color => handleThemeOverride('palette.textPrimary', color)}
                            />
                        </Box>

                        <Box>
                            <Typography variant="body2" gutterBottom>{t('Secondary Text Color')}</Typography>
                            <CColorPicker
                                value={question.theme?.palette?.textSecondary}
                                onChange={color => handleThemeOverride('palette.textSecondary', color)}
                            />
                        </Box>
                    </Stack>
                </AccordionDetails>
            </Accordion>

            <Dialog
                open={imageDialogOpen}
                onClose={() => setImageDialogOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>{t('Background Image')}</DialogTitle>
                <DialogContent sx={{pt: 1}}>
                    <ImagePicker
                        image={question.theme?.backgroundMedia}
                        setImage={handleImageChange}
                        height={260}
                        width="100%"
                    />
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default QuestionSettingsPanel;
